import React, { useState, useEffect } from 'react';
import { getSoftwareAsset } from '../../../../models/assetsModel';
import Modal from '../../../../components/common/Modal';
import Button from '../../../../components/common/Button';

// Helper function to strip HTML tags
const stripHtmlTags = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Helper function to calculate days remaining
const calculateDaysRemaining = (expiryDate) => {
  if (!expiryDate) return 0;
  const today = new Date();
  const expiry = new Date(expiryDate);
  
  if (isNaN(expiry.getTime())) return 0;
  
  const diffTime = expiry - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

function ViewSoftwareModal({ isOpen, onClose, assetId }) {
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && assetId) {
      fetchAssetDetails();
    }
  }, [isOpen, assetId]);

  const fetchAssetDetails = async () => {
    try {
      setLoading(true);
      const data = await getSoftwareAsset(assetId);
      setAsset(data);
    } catch (error) {
      console.error('Error fetching software asset details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAsset(null);
    onClose();
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Software Asset Details">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#0087D4' }}></div>
        </div>
      </Modal>
    );
  }

  if (!asset) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Software Asset Details">
        <div className="text-center py-8">
          <p className="text-gray-500">No asset details available</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Software Asset Details">
      <div className="space-y-4">
        {/* Header with Icon and Title */}
        <div className="flex items-center space-x-3 pb-3 border-b border-gray-200">
          <div className="flex-shrink-0 rounded-lg p-2.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
            <svg className="h-6 w-6" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{asset.asset_name}</h3>
            {asset.vendor && <p className="text-sm text-gray-500">{asset.vendor}</p>}
          </div>
        </div>

        {/* Basic Information */}
        <div className="bg-blue-50 rounded-lg p-3">
          <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Basic Information
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white rounded-md p-2">
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Software Name</dt>
              <dd className="mt-1 text-sm text-gray-900">{asset.asset_name || 'N/A'}</dd>
            </div>
            <div className="bg-white rounded-md p-2">
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Category</dt>
              <dd className="mt-1 text-sm text-gray-900">{asset.category || 'N/A'}</dd>
            </div>
            <div className="bg-white rounded-md p-2">
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Vendor</dt>
              <dd className="mt-1 text-sm text-gray-900">{asset.vendor || 'N/A'}</dd>
            </div>
            <div className="bg-white rounded-md p-2">
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Cost</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {asset.asset_cost ? `R ${parseFloat(asset.asset_cost).toLocaleString()}` : 'N/A'}
              </dd>
            </div>
          </div>
        </div>

        {/* License Information */}
        <div className="bg-green-50 rounded-lg p-3">
          <h4 className="text-sm font-semibold text-green-900 mb-2 flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            License Information
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white rounded-md p-2">
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">License Key</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono">{asset.asset_license_key || 'N/A'}</dd>
            </div>
            <div className="bg-white rounded-md p-2">
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Licensed Users</dt>
              <dd className="mt-1 text-sm text-gray-900">{asset.asset_users || 'N/A'}</dd>
            </div>
            <div className="bg-white rounded-md p-2">
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Purchase Date</dt>
              <dd className="mt-1 text-sm text-gray-900">{asset.asset_date_purchased || 'N/A'}</dd>
            </div>
            <div className="bg-white rounded-md p-2">
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Expiry Date</dt>
              <dd className="mt-1 text-sm text-gray-900">{asset.asset_expiry_date || 'N/A'}</dd>
            </div>
          </div>
          {asset.asset_expiry_date && (
            <div className="mt-3 bg-white rounded-md p-2">
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Days Remaining</dt>
              <dd className={`mt-1 text-lg font-semibold ${
                calculateDaysRemaining(asset.asset_expiry_date) <= 30 
                  ? 'text-red-600' 
                  : calculateDaysRemaining(asset.asset_expiry_date) <= 90 
                  ? 'text-amber-600' 
                  : 'text-green-600'
              }`}>
                {calculateDaysRemaining(asset.asset_expiry_date) || 0} days
              </dd>
            </div>
          )}
        </div>

        {/* Description */}
        {asset.asset_description && (
          <div className="bg-gray-50 rounded-lg p-3">
            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              </svg>
              Description
            </h4>
            <div className="bg-white rounded-md p-2">
              <p className="text-sm text-gray-900 leading-relaxed">
                {stripHtmlTags(asset.asset_description)}
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default ViewSoftwareModal;
